/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 */

import { type Theme, css } from '@emotion/react'
import {
  BREAKPOINT_580,
  BREAKPOINT_870,
  BREAKPOINT_1440,
  BREAKPOINT_MOBILE,
} from '@zcode-ui/themes'

export const infoContent = css`
  display: flex;
  flex: 1 1;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 22px;

  border-radius: 0 0 8px 8px;
`

export const price = ({ colors }: Theme) => css`
  margin-right: 10px;

  color: ${colors.text};
  font-size: 22px;
`

export const detailBtn = css`
  height: 20px;
  font-size: 14px;
  letter-spacing: -0.2px;
  text-decoration-line: underline;
  margin-right: 4px;
`

export const detailBtn2 = ({ colors }: Theme) => css`
  color: ${colors.textLightest};
  height: 24px;
  line-height: 24px;
  font-size: 16px;
  letter-spacing: -0.2px;
  text-decoration-line: underline;
  margin-right: 4px;
`

export const typeAndLocationText = css`
  text-overflow: ellipsis;
  overflow: hidden;
`

export const topRow = ({ colors, typography }: Theme) => css`
  display: flex;
  flex-direction: column;
  width: 100%;

  color: ${colors.textLightest};
  font-weight: ${typography.fontWeightLight};
  font-size: ${typography.fontSize14};
  line-height: ${typography.fontSize20};
  letter-spacing: -0.2px;
  text-align: left;

  @media (max-width: ${BREAKPOINT_1440}) {
    line-height: ${typography.fontSize20};
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    line-height: ${typography.fontSize24};
    font-size: ${typography.fontSize16};
  }
`

export const favoriteContainer = css`
  align-self: flex-start;
  height: 100%;
`

export const bedBathText = ({ typography }: Theme) => css`
  display: flex;
  align-items: center;
  font-size: ${typography.fontSize16};
  line-height: 24px;
  margin-top: 4px;

  cursor: default;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: ${typography.fontSize16};
    margin-top: 2px;
  }
`

export const amenitiesText = css`
  cursor: default;
`
export const typeAndLocation = ({ colors, typography }: Theme) => css`
  color: ${colors.textLightest};
  font-size: ${typography.fontSize14};
  font-weight: ${typography.fontWeightLight};
  line-height: ${typography.fontSize20};
  letter-spacing: -0.2px;
  text-align: left;
  cursor: default;
  margin-bottom: 8px;
  display: flex;
  align-items: center;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    line-height: ${typography.fontSize20};
  }

  span {
    color: ${colors.text};
    font-weight: ${typography.fontWeightBold};
  }
`

export const gridTypeAndLocation = ({ typography }: Theme) => css`
  white-space: nowrap;
  margin-top: 4px;
  margin-bottom: 0px;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: ${typography.fontSize16};
  }

  @media (max-width: ${BREAKPOINT_580}) {
    margin-top: 8px;
  }
`

export const propertyName = ({ colors, typography }: Theme) => css`
  width: 100%;
  overflow: hidden;

  color: ${colors.text};
  font-weight: ${typography.fontWeightBold};
  font-size: ${typography.fontSize20};
  line-height: ${typography.fontSize24};
  letter-spacing: -0.01em;

  white-space: nowrap;
  text-align: left;
  text-overflow: ellipsis;

  cursor: pointer;
`

export const middle = ({ colors, typography }: Theme) => css`
  color: ${colors.textLightest};
  font-size: ${typography.fontSize14};
  font-weight: 400;
  line-height: ${typography.fontSize20};
  letter-spacing: -0.2px;
  text-align: left;
`

export const hideContent = css`
  display: none;
`

export const mainContent = css`
  height: 100%;
  padding: 16px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: ${BREAKPOINT_870}) {
    padding: 15px 20px 20px;
  }

  @media (max-width: ${BREAKPOINT_580}) {
    padding: 8px 0 20px;
  }
`

export const extraContent = ({ colors }: Theme) => css`
  position: relative;
  display: flex;
  flex-direction: column;

  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.2px;
  text-align: left;
  color: ${colors.textLightest};

  height: 100%;

  padding: 32px 24px;
`

export const amenitiesHeader = ({ colors, typography }: Theme) => css`
  line-height: 24px;
  letter-spacing: -0.2px;
  text-align: left;
  color: ${colors.text};
  font-weight: ${typography.fontWeightBold};
  font-size: ${typography.fontSize16};
  margin-bottom: 4px;
`

export const moreLink = ({ colors, typography }: Theme) => css`
  font-size: ${typography.fontSize16};

  padding: 0;
  border: 0;
  background: 0;
  color: ${colors.textLightest};
  text-decoration: underline;
  cursor: pointer;

  :hover {
    color: ${colors.text};
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: ${typography.fontSize16};
  }
`

export const priceAndLogo = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  img {
    padding-bottom: 4px;
  }

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    padding-top: 12px;
  }
`

export const logoContainer = css`
  max-width: 130px;
  display: flex;
  align-items: center;
  position: relative;
  padding-bottom: 2px;
  img {
    height: initial;
    max-width: 100px;
    width: 100%;
    align-self: flex-end;
    cursor: pointer;
    max-height: 20px;
  }
`

export const minStayBlurb = ({ colors, typography }: Theme) => css`
  font-size: ${typography.fontSize14};
  font-weight: ${typography.fontWeightMedium};
  line-height: ${typography.fontSize20};
  letter-spacing: -0.2px;
  text-align: left;
  color: ${colors.textLightest};
`
export const priceContainer = ({ colors, typography }: Theme) => css`
  display: flex;
  flex-direction: column;

  color: ${colors.textLightest};
  font-weight: ${typography.fontWeightLight};
  font-size: ${typography.fontSize14};
  line-height: ${typography.fontSize20};
  letter-spacing: -0.2px;
  text-align: right;
`

export const gridPriceContainer = ({ typography }: Theme) => css`
  font-size: ${typography.fontSize16};
  line-height: ${typography.fontSize24};
`

export const rateHeader = css`
  text-align: left;
  font-size: 16px;

  @media (min-width: ${BREAKPOINT_MOBILE}) {
    font-size: 14px;
  }
`

export const rateText = ({ colors, typography }: Theme) => css`
  color: ${colors.text};
  font-weight: ${typography.fontWeightBold};
  font-size: ${typography.fontSize20};
  line-height: ${typography.fontSize24};
  letter-spacing: 0;
  text-align: right;
  cursor: pointer;

  @media (max-width: ${BREAKPOINT_MOBILE}) {
    font-size: ${typography.fontSize24};
  }
`

export const durationText = ({ colors }: Theme) => css`
  color: ${colors.text};
  cursor: pointer;
`

export const totalCostText = css`
  text-decoration: underline dashed;
`

export const gridTotalCost = ({ typography }: Theme) => css`
  font-size: ${typography.fontSize14};
  line-height: ${typography.fontSize20};
`

export const formerRateText = css`
  text-decoration: line-through;
`

export const amenitiesSection = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`

export const amenitiesList = ({ typography }: Theme) => css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 6px;
  height: min-content;
  font-size: ${typography.fontSize16};
  line-height: 24px;
  white-space: nowrap;
`

export const amenitiesItem = css`
  text-overflow: ellipsis;
  overflow: hidden;
`

export const otherSpacesText = ({ typography }: Theme) => css`
  font-size: ${typography.fontSize16};
  line-height: 24px;
`

export const closeButtonWrapper = css`
  position: absolute;
  top: 0;
  right: 0;

  button {
    height: 44px;
    width: 44px;
  }
`

export const mainSection = css`
  display: flex;
`
export const favoriteBtnListView = css`
  position: absolute;
  top: 12px;
  right: 12px;
`

export const clickOutCTA = (showIcon: boolean) => css`
  margin-left: 16px;
  overflow: hidden;

  & > span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  svg {
    margin-right: 3px;
  }

  @media (min-width: ${BREAKPOINT_870}) and (max-width: ${BREAKPOINT_1440}),
    (min-width: ${BREAKPOINT_580}) and (max-width: 700px) {
    padding: 0 12px;
    svg {
      display: ${!showIcon ? 'none' : 'inline-block'};
    }
  }
`

export const spacing = css`
  height: 20px;
`
